// Imports
import { faTriangleExclamation, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import LinkButton from '../../../shareables/foundation/front-end/components/buttons/link';
import DateTimeCell from '../../../shareables/foundation/front-end/components/tables/date-time-cell';
import theme from '../../../shareables/theme';
import { GetBillingResponse } from '../../../shareables/types/api/main/reseller/billing/get';
import formatCurrency from '../../../shareables/utils/project-specific/formatting/format-currency';
import ResellerContext from '../../resources/reseller-provider/context';


// Styled component
const ReversalNote = styled.div`
	margin-top: 0.1em;
	color: ${theme.colors.danger};
	
	> svg {
		margin-right: 0.7em;
	}
`;


// Define the accepted props
interface Props {
	transaction: GetBillingResponse['transactions'][0];
}


// Function component
const TransactionRow: React.FC<Props> = ({ transaction }) => {
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Build date cell's JSX
	let dateJSX = (
		<td>
			<em>Processing</em>
			<small className='is-gray'>
				Initiated:{' '}
				{new Date(transaction.initiated).toLocaleDateString(undefined, {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
				})}
			</small>
		</td>
	);
	
	if (transaction.received) {
		dateJSX = <DateTimeCell dateTime={transaction.received} />;
	}
	
	if (transaction.failed) {
		dateJSX = (
			<td>
				<em>Failed</em>
				<small className='is-gray'>
					Initiated:{' '}
					{new Date(transaction.initiated).toLocaleDateString(undefined, {
						month: 'short',
						day: 'numeric',
						year: 'numeric',
					})}
				</small>
			</td>
		);
	}
	
	
	// Build content
	const { dateTimeCell, descriptor, paymentMethod, transactionID } = {
		dateTimeCell: dateJSX,
		descriptor: (
			<React.Fragment>
				{formatCurrency(transaction.amount, {
					hideCentsIfInteger: true,
					currency: 'USD',
					hideCurrencyCode: (reseller?.country?.code ?? 'USA') === 'USA',
				})}{' '}
				for “{transaction.descriptor}”
				{(transaction.refunded || transaction.disputed || transaction.returned) && (
					<ReversalNote>
						<FontAwesomeIcon icon={faTriangleExclamation} />
						
						<span>
							{[
								...(transaction.refunded ? ['Refunded'] : []),
								...(transaction.disputed ? ['Disputed'] : []),
								...(transaction.returned ? ['Returned'] : []),
							].join('/')}
						</span>
					</ReversalNote>
				)}
			</React.Fragment>
		),
		paymentMethod: transaction.paymentMethod.descriptor,
		transactionID: transaction.id,
	};
	
	
	// Return JSX
	return (
		<tr>
			{dateTimeCell}
			
			<td className='allow-wrapping'>{descriptor}</td>
			
			<td className='allow-wrapping'>{paymentMethod}</td>
			
			<td style={{ textAlign: 'center' }}>
				{transaction.received && (
					<LinkButton
						to={`transaction/${String(transactionID)}/receipt`}
						color='link'
						aria-label={`View receipt for “${transaction.descriptor}”`}
						smaller
					>
						<FontAwesomeIcon icon={faMagnifyingGlass} fixedWidth />
					</LinkButton>
				)}
			</td>
		</tr>
	);
};

export default TransactionRow;
